import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { NavIcon, type IconName } from './navIcons.js';
import { useLogout } from '../hooks/useLogout.js';
import { useSession } from '../store/session.js';

interface TabItem {
  to: string;
  label: string;
  icon: IconName;
  /** 仅平台管理员可见（管理后台入口） */
  adminOnly?: boolean;
  /** 仅指定角色可见（如资讯入口仅组织者/管理员） */
  roles?: ('organizer' | 'admin')[];
}

/** 底部常驻的四个高频入口（拇指可达区）。 */
const TABS: TabItem[] = [
  { to: '/profile', label: '画像', icon: 'profile' },
  { to: '/find', label: '找队友', icon: 'find' },
  { to: '/team', label: '队伍', icon: 'team' },
  { to: '/events', label: '赛事', icon: 'events' },
];

/** 「更多」面板里的完整导航：与桌面侧边栏同一套分组（用户旅程顺序），被收起的功能页仍可深链直达。 */
const MORE_GROUPS: { label: string; items: TabItem[] }[] = [
  {
    label: '发现赛事',
    items: [
      { to: '/events', label: '赛事联动', icon: 'events' },
      { to: '/news', label: '竞赛资讯', icon: 'news', roles: ['organizer', 'admin'] },
    ],
  },
  {
    label: '组队中',
    items: [
      { to: '/profile', label: '我的画像', icon: 'profile' },
      { to: '/find', label: '找队友', icon: 'find' },
      { to: '/assistant', label: 'AI 队友', icon: 'assistant' },
    ],
  },
  {
    label: '成队后',
    items: [
      { to: '/team', label: '我的队伍', icon: 'team' },
      { to: '/messages', label: '消息', icon: 'messages' },
      { to: '/tasks', label: '待办看板', icon: 'tasks' },
      { to: '/advisors', label: '指导老师', icon: 'advisor' },
    ],
  },
  {
    label: '系统',
    items: [
      { to: '/settings', label: '设置与合规', icon: 'settings' },
      { to: '/admin', label: '管理后台', icon: 'admin', adminOnly: true },
    ],
  },
];

/**
 * 移动端底部标签栏（≤860px 显示，桌面隐藏）：
 * 四个高频 Tab 常驻拇指区，其余入口收进「更多」上拉面板，避免顶部横滑导航难发现的问题。
 */
export function MobileTabBar() {
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  const logout = useLogout();
  const user = useSession((state) => state.user);

  // 管理后台入口只对 admin 显示；资讯入口只对 organizer/admin 显示（与桌面侧边栏同一套过滤规则）
  const visibleGroups = MORE_GROUPS.map((group) => ({
    label: group.label,
    items: group.items.filter(
      (item) =>
        (item.adminOnly !== true || user?.role === 'admin') &&
        (item.roles === undefined || (user?.role !== undefined && (item.roles as readonly string[]).includes(user.role))),
    ),
  }));

  // 当前页不在四个常驻 Tab 内（如 AI 队友 / 设置，或被收起的功能页深链）时，「更多」保持高亮
  const inMainTab = TABS.some(
    (tab) => location.pathname === tab.to || location.pathname.startsWith(`${tab.to}/`),
  );

  // 路由切换后自动收起面板；Escape 关闭
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setMoreOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [moreOpen]);

  return (
    <>
      <nav className="tabbar" aria-label="移动端主导航">
        {TABS.map((tab) => (
          <NavLink key={tab.to} to={tab.to} className={({ isActive }) => (isActive ? 'tabbar-item active' : 'tabbar-item')}>
            <NavIcon name={tab.icon} className="tabbar-ico" />
            <span className="tabbar-label">{tab.label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className={moreOpen || !inMainTab ? 'tabbar-item active' : 'tabbar-item'}
          aria-expanded={moreOpen}
          aria-haspopup="dialog"
          onClick={() => setMoreOpen((v) => !v)}
        >
          <svg
            className="tabbar-ico"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
            <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
            <circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" />
          </svg>
          <span className="tabbar-label">更多</span>
        </button>
      </nav>

      {moreOpen ? (
        <div className="sheet-overlay" role="presentation" onClick={() => setMoreOpen(false)}>
          <div
            className="sheet"
            role="dialog"
            aria-modal="true"
            aria-label="更多导航"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sheet-grab" aria-hidden="true" />
            <div className="sheet-title">全部功能</div>
            <nav className="sheet-nav" aria-label="全部功能导航">
              {visibleGroups.map((group) => (
                <div key={group.label} className="sheet-group">
                  <div className="sheet-group-label" aria-hidden="true">
                    {group.label}
                  </div>
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) => (isActive ? 'sheet-link active' : 'sheet-link')}
                    >
                      <NavIcon name={item.icon} className="sheet-ico" />
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
            <button type="button" className="sheet-logout" onClick={logout}>
              退出登录
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
